import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const NotificationSettings = ({ token }) => {
  const [settings, setSettings] = useState({
    notify_on_signup: true,
    notify_on_review: true,
    notify_on_dive_report: true,
    notify_on_contact: true,
    notify_on_invite_request: true,
    notify_on_species_submission: true,
    notify_on_new_thread: true,
    notify_on_new_trip: true,
    notification_email: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/notification-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (error) {
      console.error("Failed to load notification settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      await axios.put(
        `${API}/admin/notification-settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notification settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleSetting = (key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-gray-500">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  const notificationOptions = [
    {
      key: "notify_on_signup",
      label: "New Member Signups",
      description: "Get notified when a new user registers"
    },
    {
      key: "notify_on_review",
      label: "New Reviews",
      description: "Get notified when someone submits a review"
    },
    {
      key: "notify_on_dive_report",
      label: "Dive Site Reports",
      description: "Get notified when members submit dive site reports"
    },
    {
      key: "notify_on_contact",
      label: "Contact Form Submissions",
      description: "Get notified for contact form messages"
    },
    {
      key: "notify_on_invite_request",
      label: "Invitation Requests",
      description: "Get notified when someone requests an invitation to join"
    },
    {
      key: "notify_on_species_submission",
      label: "Species Submissions",
      description: "Get notified when a member submits a species for identification"
    },
    {
      key: "notify_on_new_thread",
      label: "New Forum Threads",
      description: "Get notified when a new forum thread is created"
    },
    {
      key: "notify_on_new_trip",
      label: "New Trip Proposals",
      description: "Get notified when a member proposes a new trip"
    }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Email Notification Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {notificationOptions.map((option) => (
            <div
              key={option.key}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
            >
              <div>
                <p className="font-medium">{option.label}</p>
                <p className="text-sm text-gray-500">{option.description}</p>
              </div>
              <button
                onClick={() => toggleSetting(option.key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings[option.key] ? "bg-cyan-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings[option.key] ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          ))}
        </div>

        <div className="pt-4 border-t">
          <Label>Override Notification Email (optional)</Label>
          <p className="text-sm text-gray-500 mb-2">
            Leave empty to use your admin email address
          </p>
          <Input
            type="email"
            value={settings.notification_email || ""}
            onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
            placeholder="alternate@email.com"
          />
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-cyan-500 hover:bg-cyan-600"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
